/**
 * Pure step builder for the superuser Bulk Update page. Mirrors the exact calls the
 * camera dialogue's Configuration and AI Setup tabs make, in the same order.
 */
import { modelForEnabledAi, buildshipModelValue } from "@/components/devices/tabs/ai-workflows/aiModelSettings";
import { buildPrivacyUpdates, privacyLevelValue } from "@/components/devices/tabs/ai-workflows/privacyLevel";

export interface BulkStep {
  label: string;
  /** Camera service updateDevice field + value. */
  buildship?: [string, string | number];
  /** device_details columns to write. */
  db?: Record<string, unknown>;
}

/** Only keys present are applied. */
export interface BulkChanges {
  scheduleStartLocal?: number; // hour 0-23 in each camera's own timezone
  scheduleEndLocal?: number;
  timezone?: string;
  intervalMs?: number;
  onDemand?: boolean;
  forceHibernate?: boolean;
  enable12V?: boolean;
  cameraSelect?: number;
  brightness?: number;
  contrast?: number;
  saturation?: number;
  ev?: number;
  iso?: number;
  absoluteExposure?: number;
  lightLevel?: number;
  enablePublic?: boolean;
  enableSponsorship?: boolean;
  aiEnabled?: boolean;
  aiModel?: string;
  workflow?: string;
  privacyLevel?: number;
  query?: string;
  dataTags?: string[];
}

export interface BulkDeviceInfo {
  device_id: string;
  timezone?: string | null;
  notification_ai_model?: string | null;
  customWorkflowAvailable?: boolean;
}

export const BULK_CONCURRENCY = 3;

/** Local hour in a timezone → UTC hour (whole-hour offsets, same rule as the dialogue). */
export const localHourToUtc = (hour: number, timeZone: string, now = new Date()): number => {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "numeric", hourCycle: "h23" }).formatToParts(now);
    const tzHour = Number(parts.find(p => p.type === "hour")?.value ?? now.getUTCHours());
    const offset = tzHour - now.getUTCHours();
    return (((hour - offset) % 24) + 24) % 24;
  } catch {
    return hour;
  }
};

const b = (n: boolean) => (n ? 1 : 0);

export class SkipError extends Error {}

export const buildDeviceSteps = (changes: BulkChanges, device: BulkDeviceInfo, now = new Date()): BulkStep[] => {
  const s: BulkStep[] = [];
  const tz = changes.timezone ?? device.timezone ?? "Europe/London";
  const cfg = (label: string, field: string, value: string | number | undefined) => {
    if (value !== undefined) s.push({ label, buildship: [field, value] });
  };

  // Configuration tab
  if (changes.timezone !== undefined) cfg("Timezone", "timezone", changes.timezone);
  if (changes.scheduleStartLocal !== undefined) cfg("Schedule start", "cameraConfig.scheduleStart", localHourToUtc(changes.scheduleStartLocal, tz, now));
  if (changes.scheduleEndLocal !== undefined) cfg("Schedule end", "cameraConfig.scheduleEnd", localHourToUtc(changes.scheduleEndLocal, tz, now));
  cfg("Capture interval", "cameraConfig.withinScheduleSleepTime", changes.intervalMs);
  if (changes.onDemand !== undefined) cfg("On-demand", "cameraConfig.onDemand", b(changes.onDemand));
  if (changes.forceHibernate !== undefined) cfg("Force hibernate", "cameraConfig.forceHibernate", b(changes.forceHibernate));
  if (changes.enable12V !== undefined) cfg("12V power", "cameraConfig.cameraEnable12V", b(changes.enable12V));
  cfg("Camera select", "cameraConfig.cameraSelect", changes.cameraSelect);
  cfg("Brightness", "cameraConfig.cameraBrightness", changes.brightness);
  cfg("Contrast", "cameraConfig.cameraContrast", changes.contrast);
  cfg("Saturation", "cameraConfig.cameraSaturation", changes.saturation);
  cfg("EV", "cameraConfig.cameraEV", changes.ev);
  cfg("ISO", "cameraConfig.cameraISOSensitivity", changes.iso);
  cfg("Exposure", "cameraConfig.cameraAbsoluteExposure", changes.absoluteExposure);
  cfg("Light level", "cameraConfig.cameraLightLevel", changes.lightLevel);
  if (changes.enablePublic !== undefined) cfg("Public", "enablePublic", b(changes.enablePublic));
  if (changes.enableSponsorship !== undefined) cfg("Sponsorship", "enableSponsorship", b(changes.enableSponsorship));

  // AI Setup tab
  if (changes.workflow !== undefined) {
    if (changes.workflow === "custom_workflow" && !device.customWorkflowAvailable) {
      throw new SkipError("Organisation has no custom workflow");
    }
    cfg("Workflow", "notificationAiWorkflow", changes.workflow);
  }

  const modelStep = (model: string | null): BulkStep => ({
    label: "AI model",
    buildship: ["notificationAiModel", buildshipModelValue(model)],
    db: { notification_ai_model: model },
  });
  const enableStep = (on: boolean): BulkStep => ({
    label: on ? "Enable AI" : "Disable AI",
    buildship: ["enableNotifications", b(on)],
    db: { enable_notifications: on },
  });

  if (changes.aiEnabled === true) {
    // Model is always saved before activation.
    s.push(modelStep(modelForEnabledAi(changes.aiModel ?? device.notification_ai_model)));
    s.push(enableStep(true));
  } else if (changes.aiEnabled === false) {
    s.push(enableStep(false));
    s.push(modelStep(null));
  } else if (changes.aiModel) {
    s.push(modelStep(changes.aiModel));
  }

  if (changes.privacyLevel !== undefined) {
    const [lvl, txt] = buildPrivacyUpdates(changes.privacyLevel);
    s.push({ label: "Privacy (database)", db: { device_privacy_level: changes.privacyLevel, notification_ai_privacy_level: privacyLevelValue(changes.privacyLevel) } });
    s.push({ label: "Privacy level", buildship: lvl });
    s.push({ label: "Privacy text", buildship: txt });
  }
  if (changes.query !== undefined) cfg("AI query", "notificationQuestion", changes.query);
  if (changes.dataTags !== undefined) cfg("Data tags", "notificationDataTags", JSON.stringify(changes.dataTags));

  return s;
};
